import {
    HEALTH_PLAN_PROVIDERS,
    type HealthPlanAction,
    type HealthPlanDependentState,
    type HealthPlanProvider,
    type HealthPlanSnapshot,
} from './dpRequest.types';

interface StoredDependent {
    name?: unknown;
    relationship?: unknown;
    isCovered?: unknown;
    cardNumber?: unknown;
}

interface PotentialDependent {
    name: string;
    relationship: 'Cônjuge' | 'Filho(a)';
}

export function isHealthPlanProvider(value: unknown): value is HealthPlanProvider {
    return typeof value === 'string' && (HEALTH_PLAN_PROVIDERS as readonly string[]).includes(value);
}

function potentialDependents(spouseName: unknown, children: unknown): PotentialDependent[] {
    const result: PotentialDependent[] = [];
    if (typeof spouseName === 'string' && spouseName.trim() !== '') {
        result.push({ name: spouseName.trim(), relationship: 'Cônjuge' });
    }
    if (Array.isArray(children)) {
        for (const child of children) {
            const name = (child as { name?: unknown } | null)?.name;
            if (typeof name === 'string' && name.trim() !== '') {
                result.push({ name: name.trim(), relationship: 'Filho(a)' });
            }
        }
    }
    return result;
}

export function buildHealthDependents(spouseName: unknown, children: unknown, current: unknown): HealthPlanDependentState[] {
    const stored = Array.isArray(current) ? (current as StoredDependent[]) : [];
    return potentialDependents(spouseName, children).map(dependent => {
        const existing = stored.find(item => item?.name === dependent.name && item?.relationship === dependent.relationship);
        return { name: dependent.name, relationship: dependent.relationship, isCovered: existing?.isCovered === true };
    });
}

export function buildHealthSnapshot(data: Record<string, unknown>): HealthPlanSnapshot {
    return {
        hasHealthInsurance: data.hasHealthInsurance === true,
        provider: typeof data.healthInsuranceProvider === 'string' ? data.healthInsuranceProvider : null,
        inclusionDate: typeof data.healthInsuranceInclusionDate === 'string' ? data.healthInsuranceInclusionDate : null,
        dependents: buildHealthDependents(data.spouseName, data.children, data.healthDependents),
    };
}

export function sameHealthSnapshot(a: HealthPlanSnapshot, b: HealthPlanSnapshot): boolean {
    if (a.hasHealthInsurance !== b.hasHealthInsurance || a.provider !== b.provider) return false;
    const key = (item: HealthPlanDependentState) => `${item.relationship}|${item.name}|${item.isCovered}`;
    return a.dependents.map(key).sort().join('\n') === b.dependents.map(key).sort().join('\n');
}

export interface HealthPlanChoice {
    action: HealthPlanAction;
    provider: HealthPlanProvider | null;
    includeHolder: boolean;
    dependentNames: readonly string[];
}

export function validateHealthPlanChoice(choice: HealthPlanChoice, current: HealthPlanSnapshot): string | null {
    const known = new Set(current.dependents.map(item => item.name));
    if (!choice.dependentNames.every(name => known.has(name))) {
        return 'Um dos dependentes escolhidos não está no seu cadastro.';
    }
    const covered = new Set(current.dependents.filter(item => item.isCovered).map(item => item.name));

    if (choice.action === 'include') {
        if (choice.includeHolder && current.hasHealthInsurance) return 'Você já tem plano de saúde cadastrado.';
        if (choice.includeHolder && !choice.provider) return 'Escolha a operadora.';
        if (!choice.includeHolder && !current.hasHealthInsurance) return 'Para incluir dependentes, você precisa ter o plano.';
        if (!choice.includeHolder && choice.dependentNames.length === 0) return 'Escolha quem será incluído.';
        if (choice.dependentNames.some(name => covered.has(name))) return 'Um dos dependentes já está no plano.';
        return null;
    }

    if (!current.hasHealthInsurance) return 'Você não tem plano de saúde cadastrado.';

    if (choice.action === 'change') {
        if (!choice.provider) return 'Escolha a nova operadora.';
        if (choice.provider === current.provider) return 'Escolha uma operadora diferente da atual.';
        return null;
    }

    if (!choice.includeHolder && choice.dependentNames.length === 0) return 'Escolha quem sairá do plano.';
    if (choice.dependentNames.some(name => !covered.has(name))) return 'Um dos dependentes não está no plano.';
    return null;
}

export function buildHealthPlanPatch(
    choice: HealthPlanChoice,
    current: HealthPlanSnapshot,
    effectiveDate: string,
    existingDependents: unknown
): Record<string, unknown> {
    const stored = Array.isArray(existingDependents) ? (existingDependents as StoredDependent[]) : [];
    const cardOf = (name: string, relationship: string) => {
        const match = stored.find(item => item?.name === name && item?.relationship === relationship);
        return typeof match?.cardNumber === 'string' ? match.cardNumber : '';
    };
    const withDependents = (isCovered: (dependent: HealthPlanDependentState) => boolean, clearCards = false) =>
        current.dependents.map(dependent => ({
            name: dependent.name,
            relationship: dependent.relationship,
            isCovered: isCovered(dependent),
            cardNumber: clearCards ? '' : cardOf(dependent.name, dependent.relationship),
        }));
    const chosen = new Set(choice.dependentNames);

    if (choice.action === 'include') {
        return {
            ...(choice.includeHolder
                ? {
                      hasHealthInsurance: true,
                      healthInsuranceProvider: choice.provider,
                      healthInsuranceInclusionDate: effectiveDate,
                      healthInsuranceCardNumber: null,
                  }
                : {}),
            healthDependents: withDependents(dependent => dependent.isCovered || chosen.has(dependent.name)),
        };
    }

    if (choice.action === 'change') {
        return {
            hasHealthInsurance: true,
            healthInsuranceProvider: choice.provider,
            healthInsuranceInclusionDate: effectiveDate,
            healthInsuranceCardNumber: null,
            healthDependents: withDependents(dependent => dependent.isCovered, true),
        };
    }

    if (choice.includeHolder) {
        return {
            hasHealthInsurance: false,
            healthInsuranceProvider: null,
            healthInsuranceInclusionDate: null,
            healthInsuranceCardNumber: null,
            healthDependents: withDependents(() => false, true),
        };
    }

    return { healthDependents: withDependents(dependent => dependent.isCovered && !chosen.has(dependent.name)) };
}
